/**
 * Tower (箭塔) - Square stone watchtower with Chinese architectural elements
 * Features: Stone platform base with steps, square tower body, decorative roof, red flag
 */

import * as THREE from 'three';
import { createFlag } from '../BuildingMaterials';

export function createTowerMesh(
  group: THREE.Group,
  mainColor: THREE.Color,
  accentColor: THREE.Color
): void {
  const stoneMat = new THREE.MeshStandardMaterial({ color: 0x7a7a7a, roughness: 0.9 });
  const darkStoneMat = new THREE.MeshStandardMaterial({ color: 0x5a5a5a, roughness: 0.95 });
  const lightStoneMat = new THREE.MeshStandardMaterial({ color: 0x8a8a8a, roughness: 0.85 });
  const woodMat = new THREE.MeshStandardMaterial({ color: 0x5d4037, roughness: 0.8 });
  const roofMat = new THREE.MeshStandardMaterial({ color: 0x4a4a4a, roughness: 0.7 });
  const windowMat = new THREE.MeshStandardMaterial({ color: 0x1a1a1a });
  const flagMat = new THREE.MeshStandardMaterial({
    color: 0xc62828,
    side: THREE.DoubleSide,
    roughness: 0.9
  });

  // === Large Stone Platform Base ===
  const baseGeom1 = new THREE.BoxGeometry(2.8, 0.35, 2.8);
  const base1 = new THREE.Mesh(baseGeom1, darkStoneMat);
  base1.position.y = 0.175;
  base1.castShadow = true;
  base1.receiveShadow = true;
  group.add(base1);

  // Second tier platform
  const baseGeom2 = new THREE.BoxGeometry(2.4, 0.3, 2.4);
  const base2 = new THREE.Mesh(baseGeom2, stoneMat);
  base2.position.y = 0.5;
  base2.castShadow = true;
  group.add(base2);

  // Third tier platform
  const baseGeom3 = new THREE.BoxGeometry(2.0, 0.25, 2.0);
  const base3 = new THREE.Mesh(baseGeom3, lightStoneMat);
  base3.position.y = 0.775;
  base3.castShadow = true;
  group.add(base3);

  // Stone steps on front
  for (let i = 0; i < 4; i++) {
    const stepWidth = 0.9 - i * 0.08;
    const stepGeom = new THREE.BoxGeometry(stepWidth, 0.12, 0.22);
    const step = new THREE.Mesh(stepGeom, stoneMat);
    step.position.set(0, 0.06 + i * 0.15, 1.4 + i * 0.18);
    step.castShadow = true;
    group.add(step);
  }

  // === Main Tower Body (Square) ===
  const towerGeom = new THREE.BoxGeometry(1.4, 2.4, 1.4);
  const tower = new THREE.Mesh(towerGeom, stoneMat);
  tower.position.y = 2.1;
  tower.castShadow = true;
  group.add(tower);

  // Stone texture details on tower
  const stoneDetailGeom = new THREE.BoxGeometry(1.42, 0.12, 1.42);
  for (let i = 0; i < 4; i++) {
    const detail = new THREE.Mesh(stoneDetailGeom, darkStoneMat);
    detail.position.y = 1.1 + i * 0.6;
    group.add(detail);
  }

  // === Windows (Arrow Slits) ===
  const createArrowSlit = (x: number, z: number, rotY: number) => {
    // Outer frame
    const frameGeom = new THREE.BoxGeometry(0.25, 0.45, 0.08);
    const frame = new THREE.Mesh(frameGeom, darkStoneMat);
    frame.position.set(x, 2.2, z);
    frame.rotation.y = rotY;
    group.add(frame);

    // Inner dark slit
    const slitGeom = new THREE.BoxGeometry(0.12, 0.35, 0.1);
    const slit = new THREE.Mesh(slitGeom, windowMat);
    slit.position.set(x, 2.2, z);
    slit.rotation.y = rotY;
    group.add(slit);

    // Top decorative piece
    const topGeom = new THREE.BoxGeometry(0.3, 0.08, 0.1);
    const top = new THREE.Mesh(topGeom, darkStoneMat);
    top.position.set(x, 2.48, z);
    top.rotation.y = rotY;
    group.add(top);
  };

  // Windows on all 4 sides
  createArrowSlit(0, 0.72, 0);
  createArrowSlit(0, -0.72, 0);
  createArrowSlit(0.72, 0, Math.PI / 2);
  createArrowSlit(-0.72, 0, Math.PI / 2);

  // Second level windows
  for (const pos of [{ x: 0, z: 0.72 }, { x: 0, z: -0.72 }]) {
    const frameGeom = new THREE.BoxGeometry(0.2, 0.3, 0.08);
    const frame = new THREE.Mesh(frameGeom, darkStoneMat);
    frame.position.set(pos.x, 3.0, pos.z);
    group.add(frame);
    const slit = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.2, 0.1), windowMat);
    slit.position.set(pos.x, 3.0, pos.z);
    group.add(slit);
  }

  // === Top Battlements ===
  const battlementGeom = new THREE.BoxGeometry(0.2, 0.3, 0.2);
  const battlementPositions = [
    { x: -0.55, z: -0.55 }, { x: 0, z: -0.55 }, { x: 0.55, z: -0.55 },
    { x: -0.55, z: 0.55 }, { x: 0, z: 0.55 }, { x: 0.55, z: 0.55 },
    { x: -0.55, z: 0 }, { x: 0.55, z: 0 }
  ];
  for (const pos of battlementPositions) {
    const battlement = new THREE.Mesh(battlementGeom, lightStoneMat);
    battlement.position.set(pos.x, 3.45, pos.z);
    battlement.castShadow = true;
    group.add(battlement);
  }

  // === Pyramid Roof ===
  const roofGeom = new THREE.ConeGeometry(1.1, 0.8, 4);
  const roof = new THREE.Mesh(roofGeom, roofMat);
  roof.position.y = 4.0;
  roof.rotation.y = Math.PI / 4;
  roof.castShadow = true;
  group.add(roof);

  // Roof edge
  const roofEdgeGeom = new THREE.BoxGeometry(1.6, 0.08, 1.6);
  const roofEdge = new THREE.Mesh(roofEdgeGeom, darkStoneMat);
  roofEdge.position.y = 3.62;
  group.add(roofEdge);

  // Roof top ornament
  const ornamentGeom = new THREE.SphereGeometry(0.1, 8, 6);
  const ornament = new THREE.Mesh(ornamentGeom, new THREE.MeshStandardMaterial({
    color: 0xd4af37,
    metalness: 0.6,
    roughness: 0.3
  }));
  ornament.position.y = 4.45;
  group.add(ornament);

  // === Red Flag ===
  createFlag(group, 0.6, 3.3, 0, 1.2, 0.45, 0.3, woodMat, flagMat);

  // === Decorative Corner Pillars ===
  const pillarGeom = new THREE.BoxGeometry(0.15, 2.0, 0.15);
  const pillarCorners = [
    { x: 0.65, z: 0.65 }, { x: -0.65, z: 0.65 },
    { x: 0.65, z: -0.65 }, { x: -0.65, z: -0.65 }
  ];
  for (const pos of pillarCorners) {
    const pillar = new THREE.Mesh(pillarGeom, darkStoneMat);
    pillar.position.set(pos.x, 2.3, pos.z);
    group.add(pillar);
  }

  // === Platform edge decorations ===
  const edgeGeom = new THREE.BoxGeometry(0.15, 0.15, 0.15);
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2;
    const radius = 1.3;
    const edge = new THREE.Mesh(edgeGeom, darkStoneMat);
    edge.position.set(
      Math.cos(angle) * radius,
      0.72,
      Math.sin(angle) * radius
    );
    edge.rotation.y = angle;
    group.add(edge);
  }
}
